import { fillingStatusFromValue } from 'components/utils/fillingType';
import fillingMeasurementIssues from '../../admin/inventories/issues/rules/fillingMeasurements';

// Volume measurements a full or empty row ignores (same rule as the admin issues table).
export const MEASUREMENT_KEYS = ['liquidVolume', 'liquidHeight', 'emptyHeight'];

const isBlank = (value) => value === undefined || value === null || value === '';

const ignoredKeys = (row, columnMappings) => {
  const issue = fillingMeasurementIssues(row, columnMappings)
    .find((entry) => entry.code.startsWith('measurement_ignored'));
  return new Set((issue?.values || []).map((value) => MEASUREMENT_KEYS
    .find((key) => columnMappings[key]?.csvName === value.field)));
};

// The filling change is saved at once, form entries only on submit: a measurement has a
// saved value (the row) and possibly a different unsaved entry (the form). A field is to
// resolve when either would be ignored once the row has newStatus. This single list is
// shown in the confirmation, cleared in the saved row and cleared in the form.
// Returns [{ key, field, unit, saved, entered }]; `entered` is undefined without a form.
export function fillingMeasurementsToResolve({
  itemData, columnMappings, newStatus, formValues,
}) {
  const fillingCsv = columnMappings.filling?.csvName;
  if (!fillingCsv) return [];
  const saved = { ...itemData, [fillingCsv]: newStatus };
  const entered = { ...saved };
  MEASUREMENT_KEYS.forEach((key) => {
    const csv = columnMappings[key]?.csvName;
    if (csv && formValues?.[key] && formValues[key].value !== undefined) entered[csv] = formValues[key].value;
  });
  const keys = new Set([...ignoredKeys(saved, columnMappings), ...ignoredKeys(entered, columnMappings)]);
  return MEASUREMENT_KEYS.filter((key) => keys.has(key)).map((key) => {
    const csv = columnMappings[key].csvName;
    return {
      key,
      field: csv,
      unit: columnMappings[key].unit || '',
      saved: itemData[csv],
      entered: formValues?.[key] ? formValues[key].value : undefined,
    };
  });
}

// Whether the unsaved entry differs from the saved value (both are then shown).
export const hasUnsavedEntry = ({ saved, entered }) => entered !== undefined
  && (isBlank(saved) ? !isBlank(entered) : String(saved) !== String(entered));

// Operation record clearing the given saved measurements: only those fields, with their
// exact saved value as old value, so the backend applies it like any operator edit
// (offline queue, history, undo). Null when none of them is saved.
export function clearMeasurementsRecord({
  itemData, columnMappings, containerId, newStatus, keys,
}) {
  const formValues = {};
  const oldFormValues = {};
  keys.forEach((key) => {
    const csv = columnMappings[key]?.csvName;
    if (!csv || isBlank(itemData[csv])) return;
    const unit = columnMappings[key].unit || '';
    formValues[key] = { value: '', unit };
    oldFormValues[key] = { value: itemData[csv], unit };
  });
  if (!Object.keys(formValues).length) return null;
  return {
    containerId, formValues, oldFormValues, fillingStatus: fillingStatusFromValue(newStatus),
  };
}
